import { flexStyle, rgba, textStyle } from '@themes/mixins';
import styled, { css } from 'styled-components';

import Icon from '../Icon';

import { Props } from './types';

export const Root = styled.div`
  ${flexStyle({ direction: 'column', justify: 'flex-start', wrap: 'nowrap' })};
`;
export const DropContainer = styled.label<
  Pick<Props, 'disabled' | 'error'> & { isDragActive?: boolean }
>`
  ${flexStyle({
    align: 'center',
    direction: 'column',
    justify: 'center',
    wrap: 'nowrap',
  })};
  padding: 24px 16px;
  border-radius: 6px;
  border: 1px dashed
    ${({ error, theme }) =>
      error ? theme.colors.red : theme.colors.textSecondary};
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => rgba(theme.colors.divider, 6)};
    border: 1px dashed
      ${({ error, theme }) => (error ? theme.colors.red : theme.colors.green)};
  }

  ${({ isDragActive }) =>
    isDragActive &&
    css`
      background-color: ${({ theme }) => rgba(theme.colors.divider, 6)};
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.6;
      cursor: not-allowed;

      &:hover {
        background-color: transparent;
      }
    `}
`;
export const UploadIcon = styled(Icon)`
  color: ${({ theme }) => theme.colors.green};
  background-color: ${({ theme }) => rgba(theme.colors.green, 15)};
  border-radius: 100%;
  padding: 8px;
  margin-bottom: 24px;
`;
export const InputLabel = styled.label`
  ${flexStyle({ align: 'center', wrap: 'nowrap' })};
  ${textStyle({
    color: 'textPrimary',
    lineHeight: 1,
    size: '12px',
    textAlign: 'left',
    weight: 400,
  })};
  margin-bottom: 8px;
  letter-spacing: 0.5px;
`;
export const DropLabelTitle = styled.span`
  ${textStyle({
    color: 'textPrimary',
    lineHeight: 1.2,
    size: '14px',
    textAlign: 'center',
    weight: 500,
  })};
  letter-spacing: 1.25px;
  text-transform: capitalize;
  margin-bottom: 8px;
`;
export const DropLabelDescription = styled.span`
  ${textStyle({
    color: 'textSecondary',
    lineHeight: 1.2,
    size: '12px',
    textAlign: 'center',
    weight: 400,
  })};
  letter-spacing: 0.5px;
`;
export const FileItemsContainer = styled.div`
  ${flexStyle({ direction: 'column', justify: 'flex-start', wrap: 'nowrap' })};
  margin-top: 12px;
`;
export const FileItemCard = styled.div`
  ${flexStyle({ align: 'center', wrap: 'nowrap', justify: 'space-between' })};
  background-color: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  margin-top: 12px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  padding: 12px;
`;
export const FileItemDetails = styled.div`
  ${flexStyle({
    direction: 'row',
    justify: 'flex-start',
    wrap: 'nowrap',
    align: 'center',
  })};
  gap: 12px;
`;
export const FileItemInfo = styled.div`
  ${flexStyle({ direction: 'column', wrap: 'nowrap' })};
`;
export const FileItemName = styled.div`
  ${flexStyle({ direction: 'column', wrap: 'nowrap' })};
  ${textStyle({
    color: 'textPrimary',
    lineHeight: 1.2,
    size: '14px',
    textAlign: 'left',
    weight: 400,
  })};
  margin-bottom: 6px;
`;
export const FileItemSize = styled.div`
  ${flexStyle({ direction: 'column', wrap: 'nowrap' })};
  ${textStyle({
    color: 'textSecondary',
    lineHeight: 1,
    size: '12px',
    textAlign: 'left',
    weight: 400,
  })};
`;
export const FileIcon = styled(Icon)`
  color: ${({ theme }) => theme.colors.textPrimary};
`;
export const RemoveIcon = styled(Icon)`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-left: 8px;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.red};
  }
`;
export const SuccessIcon = styled(Icon)`
  color: ${({ theme }) => theme.colors.green};
  margin-left: 8px;
`;
export const FailedIcon = styled(Icon)`
  color: ${({ theme }) => theme.colors.red};
  margin-left: 8px;
`;
export const FileItemsHead = styled.div<{ multi: boolean }>`
  ${flexStyle({
    direction: 'row',
    wrap: 'nowrap',
    justify: 'space-between',
    align: 'center',
  })};

  ${({ multi }) =>
    multi &&
    css`
      margin-top: 24px;
    `}
`;
export const FileItemsHeadTitle = styled.span`
  ${flexStyle({ direction: 'column', wrap: 'nowrap' })};
  ${textStyle({
    color: 'textPrimary',
    lineHeight: 1,
    size: '12px',
    textAlign: 'left',
    weight: 400,
  })};
`;
export const ErrorMessageContainer = styled.div`
  ${flexStyle({ align: 'center', wrap: 'nowrap' })};
  margin-top: 8px;
`;
export const AttentionIcon = styled(Icon)`
  color: ${({ theme }) => theme.colors.red};
  margin-right: 4px;
`;
export const ErrorMessage = styled.span`
  ${textStyle({
    color: 'red',
    lineHeight: 1,
    size: '12px',
    textAlign: 'left',
    weight: 400,
  })};
  letter-spacing: 0.4px;
`;

export const RequiredMark = styled.span`
  ${textStyle({
    color: 'textPrimary',
    lineHeight: 1,
    size: '12px',
    textAlign: 'left',
    weight: 400,
  })};
  margin-left: 5px;
`;

export const InlineUploader = styled.div`
  ${flexStyle({ align: 'center' })}
  gap: ${({ theme }) => theme.spacing.md};
`;

export const FileInputBox = styled.label`
  max-width: 100px;
  height: calc(
    ${({ theme }) => theme.spacing.md} + ${({ theme }) => theme.spacing.lg}
  );
  padding: 0 ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: ${({ theme }) => theme.spacing.borderRadius.sm};
  background-color: ${({ theme }) => theme.colors.green};
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => rgba(theme.colors.green, 85)};
    border-color: ${({ theme }) => theme.colors.lightGreen};
  }

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.lightGreen};
    box-shadow: 0 0 0 3px ${({ theme }) => `${theme.colors.primary}22`};
  }
`;

export const FileInput = styled.input.attrs({ type: 'file' })`
  width: fit-content;
  height: 100%;
  border: none;
  background: transparent;
  cursor: pointer;
  color: transparent;
  caret-color: transparent;

  ${({ theme }) =>
    textStyle({
      size: theme.typography.bodyFontSize.body2,
      lineHeight: 1,
      weight: 200,
    })};

  &::file-selector-button,
  &::-webkit-file-upload-button {
    ${flexStyle({ align: 'center', justify: 'center' })}
    height: 100%;
    min-width: calc(${({ theme }) => theme.spacing.md} * 1); 
    padding: 0 ${({ theme }) => theme.spacing.md};
    margin-right: ${({ theme }) => theme.spacing.sm};
    border: 0;
    border-radius: ${({ theme }) => theme.spacing.borderRadius.sm};
    background-color: transparent;
    color: ${({ theme }) => theme.colors.white};
    font-weight: ${({ theme }) => theme.typography.fontWeightBold};
    font-size: ${({ theme }) => theme.typography.bodyFontSize.body2})};
    line-height: 1;
    cursor: pointer;
    transition:
      opacity 0.2s ease,
      transform 0.03s ease,
      box-shadow 0.2s ease;

    &:hover {
      opacity: 0.92;
      box-shadow: 0 2px 6px ${({ theme }) => `${theme.colors.boxShadow}66`};
    }
    &:active {
      transform: translateY(1px);
      box-shadow: 0 1px 3px ${({ theme }) => `${theme.colors.boxShadow}59`};
    }
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      box-shadow: none;
    }
  }

  &:focus {
    outline: none;
  }
`;

export const FileName = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  display: block;
  white-space: normal;
  overflow: visible;
  overflow-wrap: anywhere;
  word-break: break-word;

  ${textStyle({
    color: 'textSecondary',
    lineHeight: 1.2,
    textAlign: 'left',
  })};
`;
